import _ from 'underscore';
import { useCallback, useContext, useMemo, useState } from 'react';

import type {
  PreferenceRecord,
  PreferenceService,
  PreferenceUpdate,
} from '@bingads-webui/common-types';
import { PreferenceContext } from '@bingads-webui-react/preference-context';

export type UserPreference<T> = Pick<PreferenceRecord<T, string>, 'Data'>;

type UserPreferenceUpdate<T> = Required<Pick<PreferenceUpdate<T, string>, 'Name' | 'Data'>>;

export type UserPreferenceService<T> = Required<Pick<
  PreferenceService<UserPreference<T>, UserPreferenceUpdate<T>>,
  'findByNameAtUserLevel' | 'setAtUserLevel'
>>;

function isUserPreferenceService<T>(value: unknown): value is UserPreferenceService<T> {
  return typeof value === 'object'
    && value !== null
    && 'findByNameAtUserLevel' in value
    && 'setAtUserLevel' in value;
}

export function useUserLevelPreference<T>(preferenceName: string, preferenceDefaultValue: T) {
  const preferenceService = useContext(PreferenceContext);

  const getPreferenceValue = useCallback(() => {
    let pref = null;
    if (isUserPreferenceService<T>(preferenceService) && _.isFunction(preferenceService.findByNameAtUserLevel)) {
      pref = preferenceService.findByNameAtUserLevel(preferenceName);
    }

    // if preferenceService doesn't have this preference, use default value
    const newValue = pref && !_.isUndefined(pref.Data) ? pref.Data : preferenceDefaultValue;
    return newValue;
  }, [preferenceDefaultValue, preferenceName, preferenceService]);

  const [preferenceValue, setPreferenceValue] = useState(() => getPreferenceValue());

  const read = useCallback(() => {
    const newValue = getPreferenceValue();
    setPreferenceValue(newValue);
    return newValue;
  }, [getPreferenceValue]);

  const update = useCallback((newValue: T) => {
    if (isUserPreferenceService<T>(preferenceService) && _.isFunction(preferenceService.setAtUserLevel)) {
      preferenceService.setAtUserLevel({
        Name: preferenceName,
        Data: newValue,
      });
    }

    setPreferenceValue(newValue);
  }, [preferenceName, preferenceService]);

  return useMemo(() => ([
    { value: preferenceValue },
    { read, update },
  ]), [preferenceValue, read, update]);
}
